import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { userPreferencesKeys } from "queries/userPreferences.keys";
import type { AppState } from "store/reducers/Reducer";
import {
  clearPendingUserPreferences,
  clearImportedLegacyPreferences,
  mergeUserPreferences,
  readCachedUserPreferences,
  readPendingUserPreferences,
  setActivePreferencesUser,
  writeCachedUserPreferences,
  writePendingUserPreferences,
} from "./storage";
import type {
  UserPreferences,
  UserPreferencesApiDocument,
  UserPreferencesApiResponse,
  UserPreferencesPatch,
} from "./types";

type UserPreferencesContextValue = {
  preferences: UserPreferences;
  patchPreferences: (patch: UserPreferencesPatch) => void;
  isHydrated: boolean;
  userId: string | null;
};

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

/** Writes made within this window go to the server as a single PATCH. */
export const PREFERENCES_FLUSH_DELAY_MS = 300;

function fromApi(document: UserPreferencesApiDocument): UserPreferences {
  return {
    ...(document.appearance && {
      appearance: {
        source: document.appearance.source,
        mode: document.appearance.mode,
        theme: document.appearance.theme,
        font: document.appearance.font,
        fontSize: document.appearance.font_size,
        savedManual: document.appearance.saved_manual
          ? {
              mode: document.appearance.saved_manual.mode,
              theme: document.appearance.saved_manual.theme,
            }
          : undefined,
      },
    }),
    ...(document.script_editor && {
      scriptEditor: {
        pageTheme: document.script_editor.page_theme,
        recentTagCategoryNames: document.script_editor.recent_tag_category_names,
      },
    }),
    ...(document.budget && {
      budget: {
        topSheetViewMode: document.budget.top_sheet_view_mode,
      },
    }),
    ...(document.onboarding && {
      onboarding: {
        dismissed: document.onboarding.dismissed,
        currentStep: document.onboarding.current_step,
        projectSetupStatus: document.onboarding.project_setup_status,
        entrySource: document.onboarding.entry_source,
        projectSetupSource: document.onboarding.project_setup_source,
        projectId: document.onboarding.project_id,
        firstValueType: document.onboarding.first_value_type,
        projectCreatedAt: document.onboarding.project_created_at,
        screenplayReadyAt: document.onboarding.screenplay_ready_at,
        firstValueCompletedAt: document.onboarding.first_value_completed_at,
      },
    }),
    ...(document.navigation && {
      navigation: {
        lastWorkspaceId: document.navigation.last_workspace_id,
      },
    }),
  };
}

function toApi(patch: UserPreferencesPatch): UserPreferencesApiDocument {
  return {
    ...(patch.appearance && {
      appearance: {
        source: patch.appearance.source,
        mode: patch.appearance.mode,
        theme: patch.appearance.theme,
        font: patch.appearance.font,
        font_size: patch.appearance.fontSize,
        saved_manual: patch.appearance.savedManual,
      },
    }),
    ...(patch.scriptEditor && {
      script_editor: {
        page_theme: patch.scriptEditor.pageTheme,
        recent_tag_category_names: patch.scriptEditor.recentTagCategoryNames,
      },
    }),
    ...(patch.budget && {
      budget: {
        top_sheet_view_mode: patch.budget.topSheetViewMode,
      },
    }),
    ...(patch.onboarding && {
      onboarding: {
        dismissed: patch.onboarding.dismissed,
        current_step: patch.onboarding.currentStep,
        project_setup_status: patch.onboarding.projectSetupStatus,
        entry_source: patch.onboarding.entrySource,
        project_setup_source: patch.onboarding.projectSetupSource,
        project_id: patch.onboarding.projectId,
        first_value_type: patch.onboarding.firstValueType,
        project_created_at: patch.onboarding.projectCreatedAt,
        screenplay_ready_at: patch.onboarding.screenplayReadyAt,
        first_value_completed_at: patch.onboarding.firstValueCompletedAt,
      },
    }),
    ...(patch.navigation && {
      navigation: {
        last_workspace_id: patch.navigation.lastWorkspaceId,
      },
    }),
  };
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const axios = useAxios({ allResponse: true });
  const authUserId = useSelector((state: AppState) => state.auth.user_id);
  const userId = authUserId || null;
  const [preferences, setPreferences] = useState<UserPreferences>(() =>
    userId ? readCachedUserPreferences(userId) : {}
  );
  const latestMutationRef = useRef(0);

  const query = useQuery<UserPreferencesApiResponse>({
    queryKey: userPreferencesKeys.byUser(userId ?? "anonymous"),
    queryFn: async () => {
      const response = await axios.get(USER_API.preferences);
      return response.data.data;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async ({ patch, sequence }: { patch: UserPreferencesPatch; sequence: number }) => {
      const response = await axios.patch(USER_API.preferences, toApi(patch));
      return {
        response: response.data.data as UserPreferencesApiResponse,
        sequence,
      };
    },
    retry: 2,
    onSuccess: ({ response, sequence }) => {
      if (!userId || sequence !== latestMutationRef.current) return;

      const canonical = fromApi(response.preferences);
      clearPendingUserPreferences(userId);
      clearImportedLegacyPreferences(userId, canonical);
      setPreferences(canonical);
      writeCachedUserPreferences(userId, canonical);
    },
  });
  const mutatePreferences = mutation.mutate;

  // Writes queue here and go out as one PATCH after a short pause. Many
  // components can patch in the same render (every useWorkspaceIdentifiers
  // instance saves the workspace once preferences hydrate), which used to send
  // one identical request each. The queue lives in memory as well as in
  // pending storage, so a write survives even when localStorage is blocked.
  const queuedPatchRef = useRef<UserPreferencesPatch>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPatch = useCallback(
    (patch: UserPreferencesPatch) => {
      if (!userId) return;
      queuedPatchRef.current = mergeUserPreferences(queuedPatchRef.current, patch);
      // A response to an earlier PATCH must not overwrite this newer write.
      latestMutationRef.current += 1;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        const queued = mergeUserPreferences(
          readPendingUserPreferences(userId),
          queuedPatchRef.current
        );
        queuedPatchRef.current = {};
        if (Object.keys(queued).length === 0) return;
        mutatePreferences({ patch: queued, sequence: latestMutationRef.current });
      }, PREFERENCES_FLUSH_DELAY_MS);
    },
    [mutatePreferences, userId]
  );

  // Signing out or switching users drops the in-memory queue. Pending storage
  // keeps it, and it is sent the next time that user's preferences load.
  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
      queuedPatchRef.current = {};
    },
    [userId]
  );

  useEffect(() => {
    latestMutationRef.current += 1;

    if (!userId) {
      setActivePreferencesUser(null);
      setPreferences({});
      return;
    }

    setActivePreferencesUser(userId);
    const cached = readCachedUserPreferences(userId);
    const pending = readPendingUserPreferences(userId);
    setPreferences(mergeUserPreferences(cached, pending));
  }, [userId]);

  useEffect(() => {
    if (!userId || !query.data) return;

    const serverPreferences = fromApi(query.data.preferences);
    clearImportedLegacyPreferences(userId, serverPreferences);
    const pending = readPendingUserPreferences(userId);
    const reconciled = mergeUserPreferences(serverPreferences, pending);
    setPreferences(reconciled);
    writeCachedUserPreferences(userId, reconciled);

    if (Object.keys(pending).length > 0) {
      flushPatch(pending);
    }
  }, [flushPatch, query.data, userId]);

  useEffect(() => {
    if (!userId) return;

    const handleOnline = () => {
      const pending = readPendingUserPreferences(userId);
      if (Object.keys(pending).length > 0) {
        flushPatch(pending);
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [flushPatch, userId]);

  const patchPreferences = useCallback(
    (patch: UserPreferencesPatch) => {
      setPreferences((current) => {
        const next = mergeUserPreferences(current, patch);
        if (userId) {
          writeCachedUserPreferences(userId, next);
        }
        return next;
      });

      if (!userId) return;

      const pending = mergeUserPreferences(readPendingUserPreferences(userId), patch);
      writePendingUserPreferences(userId, pending);
      flushPatch(patch);
    },
    [flushPatch, userId]
  );

  const value = useMemo(
    () => ({
      preferences,
      patchPreferences,
      isHydrated: !userId || query.isSuccess || query.isError,
      userId,
    }),
    [patchPreferences, preferences, query.isError, query.isSuccess, userId]
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return context;
}

export function useOptionalUserPreferences(): UserPreferencesContextValue | null {
  return useContext(UserPreferencesContext);
}
